import React, { useState } from "react";
import CiroPill from "../../../shared/CiroPill";
import classNames from "classnames";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

// Define interface for structured filter items
export interface AutopilotDisplayableFilterItem {
  label: string; // The human-readable label for the filter section
  data: any; // The raw filter data (string, array, object)
  type: AutopilotFilterType;
}

// Define filter type enums for better type safety
export enum AutopilotFilterType {
  REGULAR_TEXT = "REGULAR_TEXT",
  MULTI_ITEM = "MULTI_ITEM",
  EXPRESSION = "EXPRESSION",
  RANGE = "RANGE",
}

// Helper function to format range filter values
const formatRangeFilter = (
  range?: { lowerBound?: number | null; upperBound?: number | null } | null,
): string => {
  if (!range) return "";
  if (range.lowerBound && range.upperBound)
    return `${range.lowerBound} - ${range.upperBound}`;
  if (range.lowerBound) return `≥ ${range.lowerBound}`;
  if (range.upperBound) return `≤ ${range.upperBound}`;
  return "";
};

interface FilterItemProps {
  item: AutopilotDisplayableFilterItem;
}

const FilterItem: React.FC<FilterItemProps> = ({ item }) => {
  const [showAllPills, setShowAllPills] = useState(false);
  const MAX_VISIBLE_PILLS = 20;

  // Determine if the filter should be displayed as pills
  const shouldShowPills = (): boolean => {
    // Only MULTI_ITEM types can be pills, and only if they have multiple values
    return (
      item.type === AutopilotFilterType.MULTI_ITEM &&
      Array.isArray(item.data) &&
      item.data.length > 1
    );
  };

  // Determines if this item has too many pills and needs a show more/less button
  const hasManyPills = (): boolean => {
    return (
      item.type === AutopilotFilterType.MULTI_ITEM &&
      Array.isArray(item.data) &&
      item.data.length > MAX_VISIBLE_PILLS
    );
  };

  // Render the filter value based on its type
  const renderFilterValue = () => {
    if (shouldShowPills()) {
      const pillsArray =
        Array.isArray(item.data) &&
        item.data.every((item) => typeof item?.label === "string")
          ? item.data
          : [];
      const visiblePills =
        hasManyPills() && !showAllPills
          ? pillsArray.slice(0, MAX_VISIBLE_PILLS)
          : pillsArray;

      return (
        <div
          className={classNames("flex", "flex-wrap", "gap-2", "items-center")}
        >
          {visiblePills.map((valueItem, idx) => (
            <CiroPill key={`${item.label}-${idx}`}>{valueItem.label}</CiroPill>
          ))}

          {hasManyPills() && (
            <button
              className={classNames(
                "inline-flex",
                "items-center",
                "text-xs",
                "text-gray-600",
                "hover:text-gray-800",
                "font-medium",
                "transition-colors duration-200 ease-in-out ml-1",
              )}
              onClick={() => setShowAllPills(!showAllPills)}
            >
              {showAllPills ? (
                <>
                  <ChevronUpIcon
                    className={classNames("h-3.5", "w-3.5", "mr-0.5")}
                  />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDownIcon
                    className={classNames("h-3.5", "w-3.5", "mr-0.5")}
                  />
                  {pillsArray.length - MAX_VISIBLE_PILLS} more
                </>
              )}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className={classNames("text-sm", "text-gray-700", "py-1")}>
        {formatFilterValue()}
      </div>
    );
  };

  // Format the filter value based on its type
  const formatFilterValue = (): string => {
    switch (item.type) {
      case AutopilotFilterType.RANGE:
        return formatRangeFilter(
          item.data as {
            lowerBound?: number | null;
            upperBound?: number | null;
          } | null,
        );
      case AutopilotFilterType.MULTI_ITEM:
        // If not pills but we still have a MULTI_ITEM (single item), display the label
        return Array.isArray(item.data) && item.data.length > 0
          ? (item.data[0] as { label: string }).label
          : "";
      case AutopilotFilterType.REGULAR_TEXT:
        return item.data || "";
      case AutopilotFilterType.EXPRESSION:
        return String(item.data || "");
      default:
        return "";
    }
  };

  return (
    <div className={classNames("flex", "flex-col")}>
      <span
        className={classNames(
          "font-medium",
          "text-xs",
          "text-gray-600",
          "mb-1.5",
        )}
      >
        {item.label}
      </span>
      {renderFilterValue()}
    </div>
  );
};

export default FilterItem;
